import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Grid, Typography } from "@mui/material";
import jwt_decode from "jwt-decode";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import keyIcon from "../../../assets/images/png/key.png";
import LoginLogo from "../../../assets/images/png/login.png";
import VetLogo from "../../../assets/images/png/VetInstantNew.png";
import CustomButton from "../../../components/CustomButton";
import CustomTextField from "../../../components/CustomTextField";
import api from "../../../redux/actions/api";
import { hideLoader, showLoader } from "../../../redux/reducers/loaderSlice";
import { ErrorStrings } from "../../../util/ErrorString";
import { fetchAndStoreMandateSession } from "../../../util/fetchAndStoreMandate";
import { EmailRegex } from "../../../util/Validations";
import "./Login.css";

const initialValues = { email: "", password: "" };
const initialError = { email: false, password: false };
const initialHelp = { email: "", password: "" };

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginValues, setLoginValues] = useState(initialValues);
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrors, setLoginErrors] = useState(initialError);
  const [loginHelps, setLoginHelps] = useState(initialHelp);
  const [isError, setError] = useState(false);

  const handleChange = (e) => {
    if (e.target.name === "email") {
      emailValidation(e.target.value);
    }
    if (e.target.name === "password") {
      passValidation(e.target.value);
    }
    setLoginValues({
      ...loginValues,
      [e.target.name]: e.target.value,
    });
  };

  const emailValidation = (value) => {
    if (value === "") {
      setLoginErrors({
        ...loginErrors,
        email: true,
      });
      setLoginHelps({ ...loginHelps, email: ErrorStrings.emptyEmail });
      return;
    }
    if (!EmailRegex.test(value)) {
      setLoginErrors({
        ...loginErrors,
        email: true,
      });
      setLoginHelps({ ...loginHelps, email: ErrorStrings.inValidEmail });
      return;
    }
    setLoginErrors({
      ...loginErrors,
      email: false,
    });
    setLoginHelps({ ...loginHelps, email: "" });
  };

  const passValidation = (value) => {
    if (value === "") {
      setLoginErrors({
        ...loginErrors,
        password: true,
      });
      setLoginHelps({ ...loginHelps, password: ErrorStrings.emptyPass });
      return;
    }
    if (value.length < 6) {
      setLoginErrors({
        ...loginErrors,
        password: true,
      });
      setLoginHelps({ ...loginHelps, password: ErrorStrings.inValidPass });
      return;
    }
    setLoginErrors({
      ...loginErrors,
      password: false,
    });
    setLoginHelps({ ...loginHelps, password: "" });
  };

  const getValidation = () => {
    const emailValue = loginValues?.email.trim();
    const passValue = loginValues?.password.trim();
    let errObj = {};
    let hlpObj = {};

    if (emailValue === "" && passValue === "") {
      errObj = { ...errObj, email: true, password: true };
      hlpObj = {
        ...hlpObj,
        email: ErrorStrings.emptyEmail,
        password: ErrorStrings.emptyPass,
      };
    }
    if (emailValue !== "" && passValue === "") {
      errObj = {
        ...errObj,
        email: !EmailRegex.test(emailValue) ? true : false,
        password: true,
      };
      hlpObj = {
        ...hlpObj,
        email: !EmailRegex.test(emailValue) ? ErrorStrings.inValidEmail : "",
        password: ErrorStrings.emptyPass,
      };
    }

    if (emailValue === "" && passValue !== "") {
      errObj = {
        ...errObj,
        email: true,
        password: passValue.length < 6 ? true : false,
      };
      hlpObj = {
        ...hlpObj,
        email: ErrorStrings.emptyEmail,
        password: passValue.length < 6 ? ErrorStrings.inValidPass : "",
      };
    }

    if (emailValue !== "" && passValue !== "") {
      errObj = {
        ...errObj,
        email: !EmailRegex.test(emailValue) ? true : false,
        password: passValue.length < 6 ? true : false,
      };
      hlpObj = {
        ...hlpObj,
        email: !EmailRegex.test(emailValue) ? ErrorStrings.inValidEmail : "",
        password: passValue.length < 6 ? ErrorStrings.inValidPass : "",
      };
    }
    setLoginErrors({ ...loginErrors, ...errObj });
    setLoginHelps({ ...loginHelps, ...hlpObj });
    return Object.values(errObj).some((item) => item);
  };

  const handleLogin = async (e) => {
    if (e.keyCode === 13) {
      const res = getValidation();
      if (res) return;
      dispatch(showLoader());
      const user = {
        username: loginValues.email,
        password: loginValues.password,
      };
      try {
        const res = await api({ contentType: true }).post(
          "/clinic/login",
          user,
        );
        if (res?.status === 200) {
          if (res?.data?.accessToken?.length > 0) {
            const token = res?.data?.accessToken;
            localStorage.setItem("accessToken", token);
            // Store refresh token if provided
            if (res?.data?.refreshToken) {
              localStorage.setItem("refreshToken", res.data.refreshToken);
            }
            const decoded = jwt_decode(token);
            localStorage.setItem(
              "user",
              JSON.stringify({ id: decoded?.id, name: decoded?.name }),
            );
            const role = decoded?.role?.[0].authority;
            localStorage.setItem("role", role);
            void fetchAndStoreMandateSession(dispatch, role).catch((err) =>
              console.error(err),
            );
            dispatch(hideLoader());
            if (role === "ROLE_ADMIN") {
              navigate("/home");
            } else if (role === "ROLE_BILLING") {
              navigate("/payments");
            } else if (role === "ROLE_INVENTORY") {
              navigate("/inventory");
            } else if (role === "ROLE_GROOMER") {
              navigate("/groomdashboard");
            } else if (role === "ROLE_PHARMACY") {
              navigate("/phramacydashboard");
            } else {
              navigate("/dashboard");
            }
          }
          if (
            res?.data?.status === 404 &&
            res?.data?.message === "Email does not exist"
          ) {
            setLoginHelps({ ...loginHelps, email: "Invalid Username!" });
            setLoginErrors({ ...loginErrors, email: true });
            dispatch(hideLoader());
            return;
          }
        } else {
          dispatch(hideLoader());
        }
      } catch (err) {
        if (
          err?.status === 401 &&
          err?.response?.data?.message === "Invalid password"
        ) {
          setLoginHelps({ ...loginHelps, password: "Incorrect Password!" });
          setLoginErrors({ ...loginErrors, password: true });
        }
        dispatch(hideLoader());
      }
    }
  };

  const handleForgetPassword = () => {
    navigate("/forgot-password");
  };

  const handleClickShowPassword = (value) => {
    setShowPassword(!value);
  };

  return (
    <div className="Login-main-container" onKeyDown={handleLogin}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
          <div className="Login-right-con">
            <div className="Login-left-top">
              <img src={VetLogo} alt="" className="Login-vet-logo" />
            </div>

            <div className="mh15Per">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Typography className="txt-bold fs30 mv20 flex-start black3">
                    Welcome Back
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <CustomTextField
                    label="Username"
                    placeholder="Username"
                    name="email"
                    fullWidth
                    handleChange={handleChange}
                    value={loginValues?.email}
                    helperText={loginHelps?.email}
                    error={loginErrors?.email}
                    startIcon
                    inputIcon={<MailOutlineIcon />}
                    mandatoryField
                    labelTop
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <CustomTextField
                    label="Password"
                    placeholder="Password"
                    name="password"
                    fullWidth
                    value={loginValues?.password}
                    handleChange={handleChange}
                    showPassword={showPassword}
                    handleClickShowPassword={handleClickShowPassword}
                    helperText={loginHelps?.password}
                    error={loginErrors?.password}
                    password
                    inputIcon={<img src={keyIcon} className="key" />}
                    type="password"
                    mandatoryField
                    labelTop
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="Login-center">
                    <div className="Login-btn">
                      <CustomButton
                        text="Login"
                        onClick={() => handleLogin({ keyCode: 13 })}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="Login-center">
                    <Typography
                      onClick={handleForgetPassword}
                      className="txt-regular fs14 red4 cursor"
                    >
                      Forgot Password?
                    </Typography>
                  </div>
                </Grid>
                {isError ? (
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <div className="flex-center">
                      <Typography className="txt-regular fs14 red4">
                        {isError}
                      </Typography>
                    </div>
                  </Grid>
                ) : null}
              </Grid>
            </div>
          </div>
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
          <div className="Login-left-con" id="loginLeftCon">
            <div className="flex1-center">
              <img src={LoginLogo} alt="" className="Login-main-img" />
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default Login;
